'use client';

import { c as _c } from "react/compiler-runtime";
import { cx } from '@7f-ui';
import { shopTestId } from '@utils/utils-shop-test-ids';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { useAppSession } from 'src/lib/auth/client/app-session-provider';
import { TrustedBar } from 'src/shared/composites/trusted-shops/trusted-bar';
import type { TrustedShopsData } from 'src/shared/composites/trusted-shops/types';
import { useIntersectionObserver } from 'usehooks-ts';
import type { HeaderConfig, HeaderLabels, HeaderViewer } from './header.types';
import { HeaderLoginModal } from './header-login-modal.client';
import { HeaderNav } from './header-nav.client';
import { HeaderSearchField } from './header-search-field.client';
import * as Logo7f from './logo-7f';
type HeaderShellProps = {
  cartCount: number;
  trustedShopsData?: TrustedShopsData | null;
  viewer: HeaderViewer;
  wishlistExists: boolean;
  labels: HeaderLabels;
};
export function HeaderShell(t0) {
  const $ = _c(40);
  const {
    cartCount,
    trustedShopsData,
    viewer,
    wishlistExists,
    labels
  } = t0;
  const pathname = usePathname() ?? "/";
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [hasOpenedLoginModal, setHasOpenedLoginModal] = useState(false);
  const {
    config,
    ref
  } = useHeaderConfig(pathname);
  const {
    loading,
    user
  } = useAppSession();
  const {
    isHomePage,
    isHomePageScrolledToTop
  } = config;
  let resolvedViewer = viewer;
  if (!loading) {
    let t1;
    if ($[0] !== user) {
      t1 = user ? {
        firstName: user.firstName
      } : null;
      $[0] = user;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    resolvedViewer = t1;
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setHasOpenedLoginModal(true);
      setIsLoginModalOpen(true);
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleOpenLoginModal = t1;
  let t2;
  if ($[3] !== trustedShopsData) {
    t2 = <TrustedBar initialData={trustedShopsData} />;
    $[3] = trustedShopsData;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== isHomePage || $[6] !== isHomePageScrolledToTop) {
    t3 = cx("flex items-center bg-primary", {
      "bg-transparent text-primary": isHomePageScrolledToTop,
      "transition-colors duration-500": isHomePage
    });
    $[5] = isHomePage;
    $[6] = isHomePageScrolledToTop;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== config || $[9] !== labels.logoTitle) {
    t4 = <Logo config={config} label={labels.logoTitle} />;
    $[8] = config;
    $[9] = labels.logoTitle;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== config || $[12] !== isHomePage) {
    t5 = !isHomePage && <HeaderSearchField config={config} />;
    $[11] = config;
    $[12] = isHomePage;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== cartCount || $[15] !== config || $[16] !== labels || $[17] !== pathname || $[18] !== resolvedViewer || $[19] !== wishlistExists) {
    t6 = <HeaderNav cartCount={cartCount} pathname={pathname} config={config} labels={labels} viewer={resolvedViewer} wishlistExists={wishlistExists} onOpenLoginModal={handleOpenLoginModal} />;
    $[14] = cartCount;
    $[15] = config;
    $[16] = labels;
    $[17] = pathname;
    $[18] = resolvedViewer;
    $[19] = wishlistExists;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t4 || $[22] !== t5 || $[23] !== t6) {
    t7 = <div className="fluid-container flex flex-1 items-center justify-between gap-4 py-2 text-white sm:gap-6 sm:py-2.5">{t4}{t5}{t6}</div>;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== t3 || $[26] !== t7) {
    t8 = <div className={t3}>{t7}</div>;
    $[25] = t3;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== t2 || $[29] !== t8) {
    t9 = <header className="sticky top-0 z-50">{t2}{t8}</header>;
    $[28] = t2;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== ref) {
    t10 = <div ref={ref} aria-hidden={true} className="absolute top-0 h-px w-px" />;
    $[31] = ref;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== hasOpenedLoginModal || $[34] !== isLoginModalOpen) {
    t11 = hasOpenedLoginModal ? <HeaderLoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} /> : null;
    $[33] = hasOpenedLoginModal;
    $[34] = isLoginModalOpen;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t10 || $[37] !== t11 || $[38] !== t9) {
    t12 = <>{t9}{t10}{t11}</>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t9;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  return t12;
}
function useHeaderConfig(pathname) {
  const $ = _c(8);
  const [isScrollTop, setIsScrollTop] = useState(true);
  const t0 = pathname === "/";
  const t1 = isScrollTop && pathname === "/";
  let t2;
  if ($[0] !== isScrollTop || $[1] !== t0 || $[2] !== t1) {
    t2 = {
      isScrollTop,
      isHomePage: t0,
      isHomePageScrolledToTop: t1
    };
    $[0] = isScrollTop;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const config = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      onChange: isIntersecting => setIsScrollTop(isIntersecting)
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    ref
  } = useIntersectionObserver(t3);
  let t4;
  if ($[5] !== config || $[6] !== ref) {
    t4 = {
      config,
      ref
    };
    $[5] = config;
    $[6] = ref;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
type LogoProps = {
  config: HeaderConfig;
  label: string;
};
function Logo(t0) {
  const $ = _c(14);
  const {
    config: t1,
    label
  } = t0;
  const {
    isHomePage,
    isHomePageScrolledToTop
  } = t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = shopTestId("Header.logo");
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = !isHomePage;
  let t4;
  if ($[1] !== isHomePageScrolledToTop || $[2] !== t3) {
    t4 = cx("h-10 w-28", {
      "hidden sm:block": t3,
      "text-primary": isHomePageScrolledToTop
    });
    $[1] = isHomePageScrolledToTop;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t4) {
    t5 = <Logo7f.Long className={t4} />;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== isHomePage) {
    t6 = cx("size-8 sm:hidden", {
      hidden: isHomePage
    });
    $[6] = isHomePage;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = <Logo7f.Short className={t6} />;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== label || $[11] !== t5 || $[12] !== t7) {
    t8 = <Link href="/" className="shrink-0 text-white" data-testid={t2} aria-label={label}>{t5}{t7}</Link>;
    $[10] = label;
    $[11] = t5;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}